import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { 
  getAdmissionGrade, 
  createAdmissionGrade, 
  updateAdmissionGrade, 
  getPrograms,
  AdmissionGradeCreate, 
  AdmissionGradeUpdate,
  Program
} from '../../api/data';

const AdmissionGradeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [formData, setFormData] = useState({
    program_id: '',
    year: new Date().getFullYear().toString(),
    grade: '',
    admission_type: '',
    track: ''
  });

  useEffect(() => {
    loadPrograms();
    if (isEdit && id) {
      loadAdmissionGrade(parseInt(id));
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEdit]);

  const loadPrograms = async () => {
    try {
      const result = await getPrograms(1, 1000);
      setPrograms(result.items);
    } catch (error) {
      console.error('Failed to load programs:', error);
    }
  };

  const loadAdmissionGrade = async (gradeId: number) => {
    try {
      const grade = await getAdmissionGrade(gradeId);
      setFormData({
        program_id: grade.program_id.toString(),
        year: grade.year.toString(),
        grade: grade.grade.toString(),
        admission_type: grade.admission_type || '',
        track: grade.track || ''
      });
    } catch (error) {
      alert('Failed to load admission grade');
      navigate('/data/admission-grades');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit && id) {
        const data: AdmissionGradeUpdate = {
          program_id: parseInt(formData.program_id),
          year: parseInt(formData.year),
          grade: parseFloat(formData.grade),
          admission_type: formData.admission_type || undefined,
          track: formData.track || undefined
        };
        await updateAdmissionGrade(parseInt(id), data);
        alert('Admission grade updated successfully');
      } else {
        const data: AdmissionGradeCreate = {
          program_id: parseInt(formData.program_id),
          year: parseInt(formData.year),
          grade: parseFloat(formData.grade),
          admission_type: formData.admission_type || undefined,
          track: formData.track || undefined
        };
        await createAdmissionGrade(data);
        alert('Admission grade created successfully');
      }
      
      navigate('/data/admission-grades');
    } catch (error: any) {
      alert('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
        <div className="card p-12 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-primary-500">
            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            Loading...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold text-primary-900 tracking-tight leading-tight">
          {isEdit ? 'Edit Admission Grade' : 'Create Admission Grade'}
        </h1>
        <Link to="/data/admission-grades" className="btn-ghost text-xs gap-1">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to List
        </Link>
      </div>

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Program <span className="text-error">*</span></label>
          <select
            value={formData.program_id}
            onChange={(e) => setFormData({ ...formData, program_id: e.target.value })}
            required
            className="input-base"
          >
            <option value="">Select a program</option>
            {programs.map((program) => (
              <option key={program.id} value={program.id}>
                {program.name_ar} ({program.name_en})
              </option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Year <span className="text-error">*</span></label>
            <input
              type="number"
              value={formData.year}
              onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              required
              min="2000"
              max="2100"
              className="input-base"
              placeholder="2024"
            />
          </div>
          <div>
            <label className="label">Grade <span className="text-error">*</span></label>
            <input
              type="number"
              step="0.01"
              value={formData.grade}
              onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
              required
              min="0"
              max="100"
              className="input-base"
              placeholder="85.5"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label">Admission Type</label>
            <select
              value={formData.admission_type}
              onChange={(e) => setFormData({ ...formData, admission_type: e.target.value })}
              className="input-base"
            >
              <option value="">Select type</option>
              <option value="general">General</option>
              <option value="parallel">Parallel</option>
              <option value="international">International</option>
            </select>
          </div>
          <div>
            <label className="label">Track</label>
            <select
              value={formData.track}
              onChange={(e) => setFormData({ ...formData, track: e.target.value })}
              className="input-base"
            >
              <option value="">Select track</option>
              <option value="scientific">Scientific</option>
              <option value="literary">Literary</option>
              <option value="commercial">Commercial</option>
              <option value="industrial">Industrial</option>
            </select>
          </div>
        </div>

        <div className="flex gap-3 pt-3">
          <button type="submit" disabled={saving} className="btn-primary gap-1.5">
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
                Saving...
              </>
            ) : (isEdit ? 'Update Admission Grade' : 'Create Admission Grade')}
          </button>
          <Link to="/data/admission-grades" className="btn-secondary">Cancel</Link>
        </div>
      </form>
    </div>
  );
};

export default AdmissionGradeForm;
